import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { boothChapters, boothDisclaimer, boothMedia } from '../../data/booth';
import type { BoothMode } from '../../types/booth';
interface BoothHomeProps {
  mode: BoothMode;
  visited: string[];
  onOpen: (chapterId: string) => void;
  onFinish: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function BoothHome({
  mode,
  visited,
  onOpen,
  onFinish
}: BoothHomeProps) {
  return <div className="relative h-full overflow-y-auto no-scrollbar" data-id="element-75" data-mp="spat0yr71blz">
      <div className="relative h-[52%] min-h-[300px] overflow-hidden bg-mist-100" data-id="element-76" data-mp="nomuzt4jrui9">
        <motion.img src={boothMedia.hero} alt="" initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.8,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-77" data-mp="jnb99yosntfc" />
        <div className="relative flex h-full max-w-[55%] flex-col justify-center px-10" data-id="element-78" data-mp="ve8vxgdf6v7e">
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-[11px] font-semibold uppercase tracking-[0.24em] text-pulse-iris" data-id="element-79" data-mp="vcfmxufc3yw8">
            ESHRE 2027 · Interactive scientific experience
          </motion.p>
          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease
        }} className="mt-3 font-serif text-[46px] leading-[1.02] text-navy-900" data-id="element-80" data-mp="co0y9dshowyl">
            Every patient has a <span className="text-pulse-iris" data-id="element-81" data-mp="2nqjhoqt3hhb">different starting point.</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.6,
          ease
        }} className="mt-4 text-[17px] leading-relaxed text-slate-600" data-id="element-82" data-mp="7mbhrimhzr34">
            Which part of ovarian stimulation would you like to explore?
          </motion.p>
        </div>
      </div>

      <div className="px-10 pb-8 pt-7" data-id="element-83" data-mp="1vkvrcco3651s">
        {mode === 'rep' && <motion.p initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-5 flex items-start gap-2.5 rounded-2xl bg-navy-900 px-4 py-3 text-[13px] leading-relaxed text-white" data-id="element-84" data-mp="1qodstujcn2xw">
            <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" data-id="element-85" data-mp="2j9x4uebs06dx" />
            <span data-id="element-86" data-mp="2edpywljdquvz">
              <span className="font-semibold" data-id="element-87" data-mp="2eb3targ1upge">Rep guide · </span>
              Open with: “Which part of ovarian stimulation is most on your mind at the moment?” Then let the HCP choose the
              chapter.
            </span>
          </motion.p>}

        <ul className="grid grid-cols-4 gap-4" data-id="element-88" data-mp="ywxvp6k61xtf">
          {boothChapters.map((c, i) => {
          const done = visited.includes(c.id);
          return <motion.li key={c.id} initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.5 + i * 0.08,
            ease
          }} data-id="element-89" data-mp="35kyldw9xactm" data-mpi={i}>
                <button type="button" onClick={() => onOpen(c.id)} className="group flex h-full w-full flex-col overflow-hidden rounded-3xl border border-navy-900/[0.08] bg-white text-left transition hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-16px_rgba(15,36,66,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-90" data-mp="3ejk0tik3lbq6">
                  <div className="relative h-28 w-full overflow-hidden bg-mist-100" data-id="element-91" data-mp="21lr2638d5n0">
                    <img src={c.image} alt="" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" data-id="element-92" data-mp="64rxpeu98yu0" />
                    {done && <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-navy-900" data-id="element-93" data-mp="27vwn048ptnb6">
                        <CheckIcon className="h-3 w-3" style={{
                    color: c.accent
                  }} data-id="element-94" data-mp="18kwpju7mdkrh" />
                        Explored
                      </span>}
                  </div>
                  <div className="flex flex-1 flex-col p-4" data-id="element-95" data-mp="1mibgj3diu0lk">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{
                  color: c.accent
                }} data-id="element-96" data-mp="1hmw5ej6weisu">
                      {c.number} · {c.kicker}
                    </p>
                    <p className="mt-1.5 font-serif text-[18px] leading-tight text-navy-900" data-id="element-97" data-mp="oxynih6lmo0m">{c.title}</p>
                    <p className="mt-2 flex-1 text-[12px] leading-snug text-slate-500" data-id="element-98" data-mp="13mlfpjhbra99">{c.question}</p>
                    <ArrowUpRightIcon className="mt-3 h-4 w-4 text-navy-700 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-99" data-mp="yubididvr7wy" />
                  </div>
                </button>
              </motion.li>;
        })}
        </ul>

        <div className="mt-6 flex items-center justify-between gap-6" data-id="element-100" data-mp="2hu18d89oftag">
          <p className="max-w-[70%] text-[10px] leading-relaxed text-slate-400" data-id="element-101" data-mp="2hrolqhsz4ur2">{boothDisclaimer}</p>
          {visited.length > 0 && <button type="button" onClick={onFinish} className="shrink-0 rounded-full bg-navy-900 px-5 py-2.5 text-[13px] font-semibold text-white transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris focus-visible:ring-offset-2" data-id="element-102" data-mp="28tml43d5k44b">
              Finish visit
            </button>}
        </div>
      </div>
    </div>;
}